import { WsServerFrameSchema } from "@stahlfront/shared";
import { useStore } from "./store.js";

const URL =
  import.meta.env.VITE_WS_URL ??
  `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`;

let socket: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let lastSubscribedServer: number | null = null;

function safeSend(payload: unknown): void {
  if (!socket || socket.readyState !== socket.OPEN) return;
  try {
    socket.send(JSON.stringify(payload));
  } catch (err) {
    console.warn("[stahlfront/ws] failed to send frame", err);
  }
}

function detachSocket(): void {
  if (!socket) return;
  socket.onopen = null;
  socket.onclose = null;
  socket.onerror = null;
  socket.onmessage = null;
  socket = null;
}

function handleFrame(raw: string): void {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return;
  }
  const result = WsServerFrameSchema.safeParse(parsed);
  if (!result.success) return;
  const frame = result.data;
  if (frame.type === "production.complete") {
    window.dispatchEvent(
      new CustomEvent("stahlfront:production.complete", { detail: frame }),
    );
  } else if (frame.type === "building.upgraded") {
    window.dispatchEvent(
      new CustomEvent("stahlfront:building.upgraded", { detail: frame }),
    );
  } else if (frame.type === "research.complete") {
    window.dispatchEvent(
      new CustomEvent("stahlfront:research.complete", { detail: frame }),
    );
  }
}

export function connectWs(serverId: number): void {
  lastSubscribedServer = serverId;

  if (socket && socket.readyState === socket.OPEN) {
    safeSend({ type: "subscribe", serverId });
    return;
  }

  detachSocket();

  const next = new WebSocket(URL);
  socket = next;

  next.onopen = () => {
    useStore.getState().setWsReady(true);
    safeSend({ type: "subscribe", serverId });
  };
  next.onmessage = (ev) => {
    if (typeof ev.data === "string") handleFrame(ev.data);
  };
  next.onclose = () => {
    useStore.getState().setWsReady(false);
    if (socket === next) detachSocket();
    if (reconnectTimer === null && lastSubscribedServer !== null) {
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        if (lastSubscribedServer !== null) connectWs(lastSubscribedServer);
      }, 2000);
    }
  };
  next.onerror = () => {
    next.close();
  };
}

export function disconnectWs(): void {
  lastSubscribedServer = null;
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (socket) {
    const live = socket;
    detachSocket();
    live.close();
  }
  useStore.getState().setWsReady(false);
}
